import React from 'react'
import { tv } from 'tailwind-variants'

// In the manner of shadcn/ui's switch: a 32 × 18 track with a 16px white thumb that nearly fills it.
// Off, a light grey track; on, blue, as a checked checkbox is. It's a switch to assistive technology
// (role and state), and Space or Enter flips it, as a click does.
// The focus ring marks keyboard focus.
const toggle = tv({
  slots: {
    track: [
      'relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full border border-transparent bg-divider',
      'cursor-pointer outline-none transition-colors duration-150',
      'focus-visible:ring-3 focus-visible:ring-blue/25'
    ],
    thumb: 'block size-4 rounded-full bg-white shadow-sm translate-x-0 transition-[translate] duration-150'
  },
  variants: {
    toggled: {
      true: { track: 'bg-blue', thumb: 'translate-x-3.5' }
    },
    disabled: {
      true: { track: 'cursor-not-allowed opacity-50' }
    }
  }
})

interface ToggleProps {
  toggled: boolean
  onToggle: (toggled: boolean) => void
  disabled?: boolean
  /** What the switch turns on and off, for when no visible label is tied to it. */
  'aria-label'?: string
}

const Toggle: React.FC<ToggleProps> = ({ toggled, onToggle, disabled, 'aria-label': ariaLabel }) => {
  const styles = toggle({ toggled, disabled: !!disabled })
  const flip = () => {
    if (!disabled) onToggle(!toggled)
  }

  return (
    <div className="ml-auto shrink-0 min-w-0">
      <div
        role="switch"
        aria-checked={toggled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        className={styles.track()}
        onClick={flip}
        onKeyDown={(event) => {
          if (event.key !== ' ' && event.key !== 'Enter') return
          // Space would otherwise scroll the page.
          event.preventDefault()
          // Kept to the switch, so a parent's own key handler doesn't act on it as well.
          event.stopPropagation()
          flip()
        }}
      >
        <span className={styles.thumb()} />
      </div>
    </div>
  )
}

export default Toggle
